import React, { useMemo } from "react";
import { people as allPeople, companies as allCompanies, meetings as allMeetings } from "@/data/dummy";
import { isLovablePreview } from "@/lib/utils/environment";

export type LinkedEntity = { name: string; type: "person" | "company" | "meeting"; data: any };

interface LinkedTextProps {
  text: string;
  /**
   * Real records to link in `text`. Click handlers receive the entity's `data`,
   * so it must carry a real backend id — never demo data.
   */
  entities?: LinkedEntity[];
  onPersonClick?: (person: any) => void;
  onCompanyClick?: (company: any) => void;
  onMeetingClick?: (meeting: any) => void;
  className?: string;
}

// Demo people/companies/meetings, linked only in Lovable preview. Outside
// preview their ids reach real lookups: demo "HubSpot" has id "3", so a
// briefing mentioning HubSpot opened getAccount(id: 3) — an unrelated account.
function demoEntities(): LinkedEntity[] {
  return [
    ...allMeetings.map(m => ({ name: m.title, type: "meeting" as const, data: m })),
    ...allPeople.map(p => ({ name: p.name, type: "person" as const, data: p })),
    ...allCompanies.map(c => ({ name: c.name, type: "company" as const, data: c })),
  ];
}

/**
 * Renders text with person, company, and meeting names as clickable links,
 * plus bare URLs. Names come from `entities`; without them, only Lovable
 * preview falls back to the demo dataset.
 */
export default function LinkedText({ text, entities: entitiesProp, onPersonClick, onCompanyClick, onMeetingClick, className }: LinkedTextProps) {
  const entities = useMemo(() => {
    const source = entitiesProp ?? (isLovablePreview() ? demoEntities() : []);
    // Drop blank names (an empty alternative matches everywhere) and
    // duplicates; longer names first to avoid partial matches.
    const seen = new Set<string>();
    return source
      .map(e => ({ ...e, name: e.name?.trim() ?? "" }))
      .filter(e => {
        if (!e.name || seen.has(e.name)) return false;
        seen.add(e.name);
        return true;
      })
      .sort((a, b) => b.name.length - a.name.length);
  }, [entitiesProp]);

  const parts = useMemo(() => {
    if (!text) return [{ text, entity: null, url: null }];

    // Build a regex matching whole-word entity names and URLs
    const escaped = entities.map(e => e.name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
    const urlPattern = `https?://[^\\s)]+`;
    const patterns = escaped.length > 0
      ? `((?<!\\w)(?:${escaped.join("|")})(?!\\w)|${urlPattern})`
      : `(${urlPattern})`;
    const regex = new RegExp(patterns, "g");

    const result: { text: string; entity: typeof entities[0] | null; url: string | null }[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = regex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        result.push({ text: text.slice(lastIndex, match.index), entity: null, url: null });
      }
      const matchText = match[0];
      const isUrl = /^https?:\/\//.test(matchText);
      const entity = isUrl ? null : (entities.find(e => e.name === matchText) || null);
      result.push({ text: matchText, entity, url: isUrl ? matchText : null });
      lastIndex = regex.lastIndex;
    }
    if (lastIndex < text.length) {
      result.push({ text: text.slice(lastIndex), entity: null, url: null });
    }
    return result;
  }, [text, entities]);

  return (
    <span className={className}>
      {parts.map((part, i) => {
        if (part.url) {
          const isLinkedIn = part.url.includes("linkedin.com");
          if (isLinkedIn) {
            const slug = part.url.replace(/^https?:\/\/(www\.)?linkedin\.com\/in\//, "").replace(/\/+$/, "");
            return (
              <span key={i}>
                {"LinkedIn"}{" ("}
                <a
                  href={part.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="font-medium text-accent underline decoration-accent/30 underline-offset-2 hover:decoration-accent/60 transition"
                >
                  /{slug}
                </a>
                {")"}
              </span>
            );
          }
          const label = part.url.replace(/^https?:\/\//, "");
          return (
            <a
              key={i}
              href={part.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="font-medium text-accent underline decoration-accent/30 underline-offset-2 hover:decoration-accent/60 transition"
            >
              {label}
            </a>
          );
        }
        if (!part.entity) return <React.Fragment key={i}>{part.text}</React.Fragment>;

        const handler =
          part.entity.type === "person" ? onPersonClick :
          part.entity.type === "company" ? onCompanyClick :
          part.entity.type === "meeting" ? onMeetingClick :
          null;
        if (!handler) return <span key={i} className="font-medium">{part.text}</span>;

        return (
          <button
            key={i}
            onClick={(e) => { e.stopPropagation(); handler(part.entity!.data); }}
            className="font-medium text-foreground underline decoration-foreground/30 underline-offset-2 hover:decoration-foreground/60 transition cursor-pointer"
          >
            {part.text}
          </button>
        );
      })}
    </span>
  );
}
